import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';

import { cloneDeep, isEmpty } from 'lodash';

import { Grid, GridColumn as Column, getSelectedState } from '@progress/kendo-react-grid';
import { process as kendoProcess, filterBy, orderBy } from '@progress/kendo-data-query';
import { getter } from '@progress/kendo-react-common';

import BobTableContextMenu from '../../../components/BobTableContextMenu';
import BobActionMenu from '../../../components/BobActionMenu';
import BobLoadingMask from '../../../components/BobLoadingMask';
import BobWorkbookModeButton from '../../../components/BobWorkbookModeButton';
import BooleanCellFilter from '../../../components/CustomFilters/BooleanCellFilter';

import { dashboardContent } from '../../../localization/en-us/dashboardContent';

import * as BobActions from '../../../actions/bobActions';
import * as DashboardActions from '../../../actions/dashboardActions';
import * as RoleActions from '../../../actions/roleActions';

import classes from './Table.module.css';

const idGetter = getter('id');

const initialDataState = {
    filter: [],
    sort:   [],
    take:   10,
    skip:   0
};

const statusCellFilterValues = [{ text: 'Active', operation: 'eq', value: 'Active' }, { text: 'Inactive', operation: 'eq', value: 'Inactive' }];
const defaultItem = { text: '(All)', operator: '' };

const RoleStatusCellFilter = (props, selectedBooleanFilter, setSelectedBooleanFilter) => {
    return (
        <BooleanCellFilter
            {...props}
            data={statusCellFilterValues}
            defaultItem={defaultItem}
            filterValue={selectedBooleanFilter}
            setFilterValue={setSelectedBooleanFilter}
        />
    );
};

const RoleList = ({
    activeNavCompany,
    bobLoading,
    createRole,
    history,
    isActive,
    fetchAllRoles,
    fetchRole,
    roleAction,
    rolesList,
    rolesMeta,
    setRoleAction,
    setSelectedRole,
    setWorkBookMode,
    updateRoleStatus,
    workbookModeEnabled,
    ...props
}) => {
    const [take, setTake] = useState(10);
    const [pageable] = useState({ pageSizes: true });
    const [dataState, setDataState] = useState(initialDataState);
    const [selectedState, setSelectedState] = useState({});
    const [selectedBooleanFilter, setSelectedBooleanFilter] = useState(defaultItem);

    const init =  async () => {
        await fetchAllRoles(); 
    };

    useEffect(() => {
        if (activeNavCompany && activeNavCompany.id) init();
    }, [activeNavCompany]);

    useEffect(() => {
        if (!workbookModeEnabled) {
            initialDataState.take = take;
            setDataState(initialDataState);
            setSelectedBooleanFilter(defaultItem);
        }
    }, [workbookModeEnabled]);

    const onSelectionChange = (event) => {
        const newSelectedState = getSelectedState({
            event,
            selectedState: selectedState,
            dataItemKey:   'id',
        });

        setSelectedState(newSelectedState);
    };

    const onHeaderSelectionChange = event => {
        const checkboxElement = event.syntheticEvent.target;
        const checked = checkboxElement.checked;
        const newSelectedState = cloneDeep(selectedState);
        let selectedStateItems = {};

        const filteredData = orderBy(filterBy(rolesList, dataState.filter), dataState.sort);
        filteredData.forEach((item) => {
            newSelectedState[idGetter(item)] = checked;
            if (checked) {
                selectedStateItems[idGetter(item)] = item;
            }

        });
        if (!checked) {
            selectedStateItems = {};
        }
        setSelectedState(newSelectedState);
    };

    const handleWorkbookMode = () => {
        setWorkBookMode(!workbookModeEnabled);
    };

    const refreshGrid = () => {
        if (activeNavCompany && activeNavCompany.id) init();
    };

    const onDataStateChange = e => {
        setTake(e.dataState.take);
        setDataState(e.dataState);
    };

    const cloneRole = async (roleData) => {
        let scopes = [];
        roleData.scopes.forEach(role => {
            role.subItems.forEach(item => {
                let hasChange = false;
                let scopeItem = {
                    'applicationId':   item.applicationId,
                    'applicationName': item.applicationName,
                    'scopeFunction':   item.name,
                    'operations':      {
                        'read':   null,
                        'create': null,
                        'update': null,
                        'delete': null
                    }
                };

                Object.keys(item.operations).forEach(operationName => {
                    if (item.operations[operationName] && item.operations[operationName].checked) {
                        hasChange = true;
                        scopeItem.operations[operationName] = item.operations[operationName].id && item.operations[operationName].checked ? item.operations[operationName].id : null;
                    }
                });

                if (hasChange) {
                    scopes.push(scopeItem);
                }
            });
        });

        
        let body = {
            'roleName': roleData.roleName + ' Copy',
            'status':   roleData.status,
            'scopes':   scopes
        };

        let isCreated = await createRole(body);
        if (isCreated) {
            init();
        }
    };

    const handleCloneRole = async (role) => {
        const roleData = await fetchRole(role.id);
        cloneRole(roleData);
    };

    const actionsCell = props => {
        return (
            <td>
                <BobTableContextMenu 
                    type="Roles"
                    entityData={props.dataItem}
                    setSelectedRole={setSelectedRole}
                    handleCloneRole={handleCloneRole}
                />
            </td>
        );
    };

    const nameCell = props => {
        return (
            <td>
                <div>
                    {props.dataItem[props.field]}
                </div>
            </td>
        );
    };

    const BuildRoleStatusCellFilter = props => {
        return RoleStatusCellFilter(props, selectedBooleanFilter, setSelectedBooleanFilter);
    };

    const roleStatusChange = async (status) => {
        if (!isEmpty(selectedState)) {
            let selectedRoleIds = [];
            Object.keys(selectedState).forEach(id => {
                if (selectedState[id]) {
                    selectedRoleIds.push(id);
                }
            });

            if (!isEmpty(selectedRoleIds)) {
                let body = {
                    roleIds: selectedRoleIds,
                    status
                };

                await updateRoleStatus(body);
                init();
            }
        }
    };

    return (
        <div className={classes.Table}>
            <div className={classes.TableContainerHead}>
                <div className={classes.WorkbookModeContainer}>
                    <BobWorkbookModeButton
                        handleWorkbookMode={handleWorkbookMode}
                        workbookMode={workbookModeEnabled}
                    />
                </div>
                <div
                    className={classes.RefreshContainer}
                    aria-hidden="true"
                    role="button"
                    tabIndex={0}
                    onClick={refreshGrid}
                >
                    <span className="material-icons-outlined">refresh</span>
                </div>
                <div className={classes.ActionButtonContainer}>
                    <BobActionMenu 
                        type={'Roles'}
                        handleAddNewRole={() => setRoleAction('create')}
                        handleRoleStatusChange={(status) => roleStatusChange(status)}
                    />
                </div>
            </div>
            <Grid
                sortable
                className={classes.KendoGrid}
                data={kendoProcess(rolesList ? rolesList.map(item => ({
                    ...item,
                    'selected': selectedState[idGetter(item)],
                })) : [], dataState)}
                onDataStateChange={onDataStateChange}
                {...dataState}
                pageable={pageable}
                reorderable
                filterable={workbookModeEnabled}
                dataItemKey={'id'}
                selectedField={'selected'}
                selectable={{
                    enabled: true,
                    drag:    false,
                    cell:    false,
                    mode:    'multiple'
                }}
                onSelectionChange={onSelectionChange}
                onHeaderSelectionChange={onHeaderSelectionChange}
            >
                <Column
                    field="selected"
                    width="70px"
                    headerSelectionValue={
                        rolesList.length > 0 && rolesList.findIndex((item) => !selectedState[idGetter(item)]) === -1
                    }
                    filterable={false}
                    sortable={false}
                />
                <Column
                    field={'roleName'}
                    title={dashboardContent.roles}
                    width="400px"
                    cell={nameCell}
                />
                <Column 
                    field={'status'} 
                    title={dashboardContent.status} 
                    width="300px"
                    filter={'boolean'}
                    filterCell={BuildRoleStatusCellFilter}
                />
                <Column
                    field=""
                    title=""
                    width="auto"
                    filterable={false}
                    sortable={false}
                    cell={actionsCell}
                />
            </Grid>
            {
                bobLoading.includes('fetchAllRoles') || bobLoading.includes('fetchRole') || 
                bobLoading.includes('updateRoleStatus') || bobLoading.includes('fetchAllScopes') ?
                    <BobLoadingMask />
                    :
                    null
            }
        </div>
    );
};

const mapStateToProps = state => ({
    activeNavCompany:    state.bob.activeNavCompany,
    bobLoading:          state.bob.bobLoading,
    rolesList:           state.roles.rolesList,
    rolesMeta:           state.roles.rolesMeta,
    workbookModeEnabled: state.dashboard.workbookModeEnabled
});

const mapDispatchToProps = ({
    ...BobActions,
    ...DashboardActions,
    ...RoleActions
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(RoleList));
